/**
 * MenusView.js — Portal menu & item management
 *
 * Writes directly to Firestore using the same document structure
 * as the native POS app (ItemsEdit.js / MenusEdit.js) so that
 * all data created here is immediately available on the POS.
 *
 * Menus:  restaurants_data/{restaurantId}/menus/{normalizedId}
 * Items:  restaurants_data/{restaurantId}/items/{itemName}
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, getDocs, getDoc, doc, setDoc, updateDoc, deleteDoc, onSnapshot, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** WCAG-based auto contrast: returns '#000000' or '#ffffff' for best readability */
const getContrastTextColor = (hex) => {
  const h = (hex || '#5f799a').replace('#', '');
  if (h.length !== 6) return '#ffffff';
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const toLinear = c => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return L > 0.179 ? '#000000' : '#ffffff';
};

/** Same normalization the POS uses for menu doc IDs */
const normalizeMenuId = (name) =>
  name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const EMPTY_MENU = {
  menuName: '', menuDescription: '', imageUrl: '',
  menuGallery: false, menuOrder: 0, menuAvailability: true,
  buttonColor: '#5f799a', buttonTextColor: '',
};

const EMPTY_ITEM = {
  name: '', menu: '', type: '', price: '', description: '', imageUrl: '',
  availability: true, outOfStock: false, showPicture: false,
  isDrinkItem: false, isBarItem: false, isDessertItem: false, isAlcohol: false,
  isFeatured: false,
  defaultCourse: '',
  options: [], ingredients: [],
};

const EMPTY_OPTION = { optionName: '', required: false, multiSelect: false, selectors: [] };

const UNITS = [
  'each', 'oz', 'lbs', 'g', 'kg',
  'fl oz', 'cups', 'pints', 'quarts', 'gallons', 'liters', 'ml',
  'tsp', 'tbsp', 'slices', 'portions', 'bunches', 'cloves', 'leaves',
];

const INGREDIENT_CATEGORIES = [
  'Proteins', 'Produce', 'Dairy', 'Dry Goods', 'Beverages',
  'Oils & Fats', 'Sauces & Condiments', 'Spices & Herbs', 'Other',
];

// ── Small shared UI pieces ────────────────────────────────────────────────────

const Toggle = ({ value, onChange, label }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none">
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full shrink-0 transition-colors ${value ? 'bg-neutral-900' : 'bg-neutral-300'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
    {label && <span className="text-sm text-neutral-600">{label}</span>}
  </label>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-500 mb-1">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300";

// ── Image upload field ────────────────────────────────────────────────────────

const ImageUploadField = ({ value, onChange, restaurantId, label = 'Image' }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const storageRef = ref(storage, `${restaurantId}/menuStorage/${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      onChange(url);
    } catch (err) {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-500 mb-1">{label}</label>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="preview" className="w-14 h-14 rounded-lg object-cover border border-neutral-200 shrink-0" />
        ) : (
          <div className="w-14 h-14 rounded-lg border-2 border-dashed border-neutral-200 flex items-center justify-center shrink-0">
            <span className="text-neutral-300 text-lg">+</span>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label className={`cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium border border-neutral-300 rounded-lg transition-colors ${uploading ? 'opacity-50 cursor-not-allowed text-neutral-400' : 'text-neutral-600 hover:border-neutral-500'}`}>
            {uploading ? 'Uploading…' : value ? 'Change Image' : 'Upload Image'}
            <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" disabled={uploading} />
          </label>
          {value && !uploading && (
            <button type="button" onClick={() => onChange('')}
              className="text-sm text-neutral-400 hover:text-red-500 transition-colors text-left">
              Remove
            </button>
          )}
          {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const MenusView = ({ restaurantId }) => {
  const [subTab, setSubTab] = useState('menus'); // 'menus' | 'items'

  return (
    <div className="space-y-4">
      {/* Sub-tab bar */}
      <div className="flex gap-1 border-b border-neutral-200 pb-0">
        {['menus', 'items'].map(t => (
          <button
            key={t}
            onClick={() => setSubTab(t)}
            className={`px-5 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
              subTab === t ? 'border-neutral-900 text-neutral-900' : 'border-transparent text-neutral-400 hover:text-neutral-600'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {subTab === 'menus' && <MenusPanel restaurantId={restaurantId} />}
      {subTab === 'items' && <ItemsPanel restaurantId={restaurantId} />}
    </div>
  );
};

// ── Menus Panel ───────────────────────────────────────────────────────────────

const MenusPanel = ({ restaurantId }) => {
  const [menus, setMenus]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editingMenu, setEditingMenu] = useState(null); // null = new, object = editing
  const [form, setForm]             = useState(EMPTY_MENU);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, 'restaurants_data', restaurantId, 'menus'),
      snap => {
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.menuOrder ?? 999) - (b.menuOrder ?? 999));
        setMenus(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsub;
  }, [restaurantId]);

  const openNew = () => {
    setEditingMenu(null);
    setForm(EMPTY_MENU);
    setError('');
    setShowForm(true);
  };

  const openEdit = (menu) => {
    setEditingMenu(menu);
    setForm({
      menuName: menu.menuName || '',
      menuDescription: menu.menuDescription || '',
      imageUrl: menu.imageUrl || '',
      menuGallery: menu.menuGallery ?? false,
      menuOrder: menu.menuOrder ?? 0,
      menuAvailability: menu.menuAvailability !== false,
      buttonColor: menu.buttonColor || '#5f799a',
      buttonTextColor: menu.buttonTextColor || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.menuName.trim()) return setError('Menu name is required.');
    setSaving(true);
    setError('');
    try {
      const menuId = editingMenu ? editingMenu.id : normalizeMenuId(form.menuName);
      if (!menuId) return (setSaving(false), setError('Menu name produced an empty ID.'));

      const data = {
        menuName: form.menuName.trim(),
        menuDescription: form.menuDescription.trim(),
        imageUrl: form.imageUrl.trim(),
        menuGallery: form.menuGallery,
        menuOrder: Number(form.menuOrder) || 0,
        menuAvailability: form.menuAvailability,
        buttonColor: form.buttonColor || '#5f799a',
        buttonTextColor: form.buttonTextColor || '',
      };

      await setDoc(doc(db, 'restaurants_data', restaurantId, 'menus', menuId), data, { merge: true });
      setShowForm(false);
    } catch (err) {
      setError(err.message || 'Failed to save menu.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'restaurants_data', restaurantId, 'menus', deleteTarget.id));
    } catch (err) {
      setError(err.message || 'Failed to delete menu.');
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) return <p className="text-sm text-neutral-400 text-center py-12">Loading menus…</p>;

  return (
    <div className="space-y-4 max-w-2xl">

      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-500">{menus.length} menu{menus.length !== 1 ? 's' : ''}</p>
        {!showForm && (
          <button onClick={openNew}
            className="px-4 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 transition-colors">
            + Add Menu
          </button>
        )}
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
          <p className="text-sm font-semibold text-neutral-800">{editingMenu ? `Edit — ${editingMenu.menuName}` : 'New Menu'}</p>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Field label="Menu Name *">
                <input type="text" value={form.menuName} onChange={e => set('menuName', e.target.value)}
                  placeholder="e.g. Dinner, Lunch, Bar" className={inputCls} autoFocus />
              </Field>
            </div>
            <div className="col-span-2">
              <Field label="Description">
                <input type="text" value={form.menuDescription} onChange={e => set('menuDescription', e.target.value)}
                  placeholder="Optional description" className={inputCls} />
              </Field>
            </div>
            <div className="col-span-2">
              <ImageUploadField
                label="Menu Image (optional)"
                value={form.imageUrl}
                onChange={url => set('imageUrl', url)}
                restaurantId={restaurantId}
              />
            </div>
            <div className="col-span-2">
              <Field label="Button Color">
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    type="color"
                    value={form.buttonColor || '#5f799a'}
                    onChange={e => set('buttonColor', e.target.value)}
                    className="w-10 h-10 rounded-lg border border-neutral-300 cursor-pointer p-0.5"
                  />
                  <span
                    className="px-4 py-1.5 rounded-lg text-sm font-semibold select-none"
                    style={{
                      backgroundColor: form.buttonColor || '#5f799a',
                      color: form.buttonTextColor || getContrastTextColor(form.buttonColor || '#5f799a'),
                    }}
                  >
                    {form.menuName || 'Menu Name'}
                  </span>
                  <span className="text-xs text-neutral-400">Preview</span>
                </div>
              </Field>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-neutral-500 font-medium">Text Color:</span>
                {[
                  { label: 'Auto', value: '' },
                  { label: 'A', value: '#ffffff', bg: '#374151' },
                  { label: 'A', value: '#000000', bg: '#f3f4f6' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => set('buttonTextColor', opt.value)}
                    title={opt.value === '' ? 'Auto-contrast' : opt.value === '#ffffff' ? 'White text' : 'Black text'}
                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${
                      form.buttonTextColor === opt.value
                        ? 'border-neutral-900 ring-2 ring-neutral-400'
                        : 'border-neutral-300 hover:border-neutral-500'
                    }`}
                    style={opt.bg ? { backgroundColor: opt.bg, color: opt.value } : { backgroundColor: '#fff', color: '#999' }}
                  >
                    {opt.label}
                  </button>
                ))}
                <span className="text-xs text-neutral-400">Auto uses WCAG contrast</span>
              </div>
            </div>
            <Field label="Display Order">
              <input type="number" value={form.menuOrder} onChange={e => set('menuOrder', e.target.value)}
                min="0" className={inputCls} />
            </Field>
          </div>

          <div className="flex gap-6 pt-1">
            <Toggle value={form.menuAvailability} onChange={v => set('menuAvailability', v)} label="Available" />
            <Toggle value={form.menuGallery} onChange={v => set('menuGallery', v)} label="Show in Wesbite Gallery" />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} disabled={saving}
              className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors">
              {saving ? 'Saving…' : editingMenu ? 'Update Menu' : 'Create Menu'}
            </button>
            <button onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm text-neutral-500 hover:text-neutral-700">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Menu list */}
      {menus.length === 0 && !showForm ? (
        <div className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-neutral-200 rounded-xl">
          <p className="text-sm text-neutral-400">No menus yet. Add one to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {menus.map(menu => (
            <div key={menu.id} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-4">
              {menu.imageUrl && (
                <img src={menu.imageUrl} alt={menu.menuName}
                  className="w-10 h-10 rounded-lg object-cover shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-neutral-800">{menu.menuName}</p>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${menu.menuAvailability !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-100 text-neutral-400'}`}>
                    {menu.menuAvailability !== false ? 'Available' : 'Hidden'}
                  </span>
                  {menu.menuGallery && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">In Web Gallery</span>
                  )}
                </div>
                {menu.menuDescription && (
                  <p className="text-sm text-neutral-400 mt-0.5 truncate">{menu.menuDescription}</p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEdit(menu)}
                  className="text-sm px-3 py-1.5 border border-neutral-300 rounded-lg text-neutral-600 hover:border-neutral-500 transition-colors">
                  Edit
                </button>
                <button onClick={() => setDeleteTarget(menu)}
                  className="text-sm px-3 py-1.5 border border-red-200 rounded-lg text-red-500 hover:border-red-400 hover:text-red-700 transition-colors">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 text-center space-y-4">
            <p className="text-sm font-semibold text-neutral-800">Delete "{deleteTarget.menuName}"?</p>
            <p className="text-sm text-neutral-500">Any items linked to this menu will lose their menu assignment.</p>
            <div className="flex justify-center gap-3">
              <button onClick={handleDelete}
                className="px-5 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                Delete
              </button>
              <button onClick={() => setDeleteTarget(null)}
                className="px-5 py-2 text-sm text-neutral-500 border border-neutral-300 rounded-lg hover:border-neutral-500 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Items Panel ───────────────────────────────────────────────────────────────

const ItemsPanel = ({ restaurantId }) => {
  const [menus, setMenus]             = useState([]);
  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [menuFilter, setMenuFilter]   = useState('all');
  const [editingItem, setEditingItem] = useState(null); // null = closed, {} = new, item = editing
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError]             = useState('');

  // Derived: unique types from all loaded items
  const existingTypes = [...new Set(items.map(i => i.type).filter(Boolean))];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [menusSnap, itemsSnap] = await Promise.all([
        getDocs(collection(db, 'restaurants_data', restaurantId, 'menus')),
        getDocs(collection(db, 'restaurants_data', restaurantId, 'items')),
      ]);
      setMenus(menusSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.menuOrder ?? 999) - (b.menuOrder ?? 999)));
      setItems(itemsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('MenusView: load error', err);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const filtered = menuFilter === 'all' ? items : items.filter(i => i.menu === menuFilter);

  const openNew = () => {
    setEditingItem({ ...EMPTY_ITEM, menu: menuFilter !== 'all' ? menuFilter : '' });
    setError('');
  };

  const openEdit = (item) => {
    setEditingItem({ ...item, price: String(item.price ?? '') });
    setError('');
  };

  const handleSave = async (formItem, originalId) => {
    setError('');
    const name = formItem.name.trim();
    if (!name || !formItem.menu || !formItem.price) {
      setError('Name, menu, and price are required.');
      return false;
    }
    try {
      const data = {
        name,
        menu: formItem.menu,
        type: formItem.type === 'All' ? '' : (formItem.type || ''),
        price: parseFloat(formItem.price) || 0,
        description: formItem.description || '',
        imageUrl: formItem.imageUrl || '',
        availability: formItem.availability !== false,
        outOfStock: !!formItem.outOfStock,
        showPicture: !!formItem.showPicture,
        isDrinkItem: !!formItem.isDrinkItem,
        isBarItem: !!formItem.isBarItem,
        isDessertItem: !!formItem.isDessertItem,
        isAlcohol: !!formItem.isAlcohol,
        isFeatured: !!formItem.isFeatured,
        defaultCourse: formItem.defaultCourse || '',
        options: formItem.options || [],
        ingredients: formItem.ingredients || [],
      };

      if (originalId && originalId !== name) {
        // Name changed — create new doc, delete old (same as POS)
        await setDoc(doc(db, 'restaurants_data', restaurantId, 'items', name), data);
        await deleteDoc(doc(db, 'restaurants_data', restaurantId, 'items', originalId));
      } else {
        await setDoc(doc(db, 'restaurants_data', restaurantId, 'items', name), data);
      }
      await load();
      setEditingItem(null);
      return true;
    } catch (err) {
      setError(err.message || 'Failed to save item.');
      return false;
    }
  };

  const handleToggleOOS = async (item) => {
    const next = !item.outOfStock;
    try {
      await updateDoc(doc(db, 'restaurants_data', restaurantId, 'items', item.id), { outOfStock: next });
      await load();
    } catch (err) {
      console.error('[MenusView] toggleOOS failed:', err);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'restaurants_data', restaurantId, 'items', deleteTarget.id));
      await load();
    } catch (err) {
      setError(err.message || 'Failed to delete item.');
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) return <p className="text-sm text-neutral-400 text-center py-12">Loading items…</p>;

  return (
    <div className="space-y-4">

      {/* Top bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={menuFilter}
          onChange={e => setMenuFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none"
        >
          <option value="all">All Menus</option>
          {menus.map(m => <option key={m.id} value={m.menuName}>{m.menuName}</option>)}
        </select>
        <span className="text-sm text-neutral-400">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
        <div className="flex-1" />
        <button onClick={openNew}
          className="px-4 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 transition-colors">
          + Add Item
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Item list */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-neutral-200 rounded-xl">
          <p className="text-sm text-neutral-400">No items yet{menuFilter !== 'all' ? ` in ${menuFilter}` : ''}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-neutral-200 border border-neutral-200 rounded-xl overflow-hidden">
          {filtered.map((item) => (
            <div key={item.id} className="flex items-center gap-3 px-4 py-3 bg-white">
              {item.imageUrl ? (
                <img src={item.imageUrl} alt={item.name} className="w-10 h-10 rounded-lg object-cover shrink-0 border border-neutral-100" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-neutral-100 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-sm font-semibold text-neutral-800">{item.name}</p>
                  {item.type && (
                    <span className="text-xs text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded-full">{item.type}</span>
                  )}
                  {item.isDrinkItem && (
                    <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full">Drink</span>
                  )}
                  {item.isBarItem && (
                    <span className="text-xs text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-full">Bar</span>
                  )}
                  {item.isDessertItem && (
                    <span className="text-xs text-pink-700 bg-pink-50 border border-pink-200 px-1.5 py-0.5 rounded-full">Dessert</span>
                  )}
                  {item.isAlcohol && (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">Alcohol</span>
                  )}
                  {!item.availability && (
                    <span className="text-xs text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded-full">Unavailable</span>
                  )}
                  {item.outOfStock && (
                    <span className="text-xs text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full font-semibold">86'd</span>
                  )}
                </div>
                <p className="text-xs text-neutral-400 mt-0.5">{item.menu}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <p className="text-sm text-neutral-500">${Number(item.price).toFixed(2)}</p>
                  {item.options?.length > 0 && (
                    <p className="text-xs text-neutral-400">{item.options.length} option{item.options.length !== 1 ? 's' : ''}</p>
                  )}
                  {item.ingredients?.length > 0 && (
                    <p className="text-xs text-neutral-400">{item.ingredients.length} ingredient{item.ingredients.length !== 1 ? 's' : ''}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => handleToggleOOS(item)}
                  className={`text-xs px-2.5 py-1.5 rounded-lg border font-semibold transition-colors ${
                    item.outOfStock
                      ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
                      : 'border-neutral-200 text-neutral-500 hover:border-neutral-400'
                  }`}
                >
                  {item.outOfStock ? "86\u2019d" : '86'}
                </button>
                <button onClick={() => openEdit(item)}
                  className="text-sm px-3 py-1.5 border border-neutral-300 rounded-lg text-neutral-600 hover:border-neutral-500 transition-colors">
                  Edit
                </button>
                <button onClick={() => setDeleteTarget(item)}
                  className="text-sm px-3 py-1.5 border border-red-200 rounded-lg text-red-500 hover:border-red-400 hover:text-red-700 transition-colors">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Item form modal */}
      {editingItem && (
        <ItemFormModal
          item={editingItem}
          menus={menus}
          existingTypes={existingTypes}
          restaurantId={restaurantId}
          error={error}
          onSave={handleSave}
          onClose={() => { setEditingItem(null); setError(''); }}
        />
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 text-center space-y-4">
            <p className="text-sm font-semibold text-neutral-800">Delete "{deleteTarget.name}"?</p>
            <div className="flex justify-center gap-3">
              <button onClick={handleDelete}
                className="px-5 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                Delete
              </button>
              <button onClick={() => setDeleteTarget(null)}
                className="px-5 py-2 text-sm text-neutral-500 border border-neutral-300 rounded-lg hover:border-neutral-500 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Item Form Modal ───────────────────────────────────────────────────────────

const ItemFormModal = ({ item: initialItem, menus, existingTypes, restaurantId, error: parentError, onSave, onClose }) => {
  const [item, setItem]         = useState({ ...initialItem });
  const [saving, setSaving]     = useState(false);
  const [isNewType, setIsNewType] = useState(false);
  const [error, setError]       = useState('');

  // Option builder state
  const [showOptionForm, setShowOptionForm] = useState(false);
  const [optionDraft, setOptionDraft]       = useState({ ...EMPTY_OPTION });
  const [selectorDraft, setSelectorDraft]   = useState({ name: '', price: '' });

  // Course state
  const [coursesEnabled, setCoursesEnabled] = useState(false);
  const [restaurantCourses, setRestaurantCourses] = useState([]);

  useEffect(() => {
    getDoc(doc(db, 'restaurants_data', restaurantId))
      .then(snap => {
        if (snap.exists()) {
          const d = snap.data();
          setCoursesEnabled(!!d.coursesEnabled);
          setRestaurantCourses(d.courses || []);
        }
      })
      .catch(() => {});
  }, [restaurantId]);

  // Ingredient picker state
  const [allIngredients, setAllIngredients] = useState([]);
  const [ingPickerOpen, setIngPickerOpen]   = useState(false);
  const [ingSearch, setIngSearch]           = useState('');
  const [pickedIng, setPickedIng]           = useState(null);
  const [ingQty, setIngQty]                 = useState('');
  const [isNewIng, setIsNewIng]             = useState(false);
  const [newIngDraft, setNewIngDraft]       = useState({ name: '', unit: 'each', category: 'Other' });
  const [ingError, setIngError]             = useState('');
  const [ingCreating, setIngCreating]       = useState(false);

  const originalId = initialItem.id || null; // null when creating new
  const isNew = !originalId;

  // Load existing ingredients for this restaurant
  useEffect(() => {
    getDocs(collection(db, 'restaurants_data', restaurantId, 'ingredients'))
      .then(snap => setAllIngredients(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => {});
  }, [restaurantId]);

  const filteredIngs = ingSearch.trim()
    ? allIngredients.filter(i => i.name.toLowerCase().includes(ingSearch.toLowerCase()))
    : allIngredients;

  const resetIngPicker = () => {
    setIngPickerOpen(false); setIngSearch(''); setPickedIng(null);
    setIngQty(''); setIsNewIng(false);
    setNewIngDraft({ name: '', unit: 'each', category: 'Other' });
    setIngError('');
  };

  const linkIngredient = () => {
    if (!pickedIng) return setIngError('Select an ingredient.');
    const qty = parseFloat(ingQty);
    if (!ingQty || isNaN(qty) || qty <= 0) return setIngError('Enter a valid quantity.');
    if ((item.ingredients || []).some(i => i.ingredientId === pickedIng.id))
      return setIngError('Already linked to this item.');
    set('ingredients', [...(item.ingredients || []), {
      ingredientId: pickedIng.id, name: pickedIng.name, quantity: qty,
      unit: pickedIng.recipeUnit || pickedIng.baseUnit || pickedIng.unit,
    }]);
    resetIngPicker();
  };

  const createAndLinkIngredient = async () => {
    if (!newIngDraft.name.trim()) return setIngError('Ingredient name is required.');
    const qty = parseFloat(ingQty);
    if (!ingQty || isNaN(qty) || qty <= 0) return setIngError('Enter a valid quantity.');
    setIngCreating(true);
    try {
      const ingRef  = doc(collection(db, 'restaurants_data', restaurantId, 'ingredients'));
      const ingName = newIngDraft.name.trim();
      const ingUnit = newIngDraft.unit;
      const ingData = {
        // Identity
        ingredientId: ingRef.id,
        name: ingName,
        displayName: ingName,
        category: newIngDraft.category,
        subcategory: '',
        description: '',
        image: '',
        barcode: '',
        sku: '',
        internalCode: '',
        active: true,

        // Units & Measurement
        baseUnit: ingUnit,
        purchaseUnit: ingUnit,
        recipeUnit: ingUnit,
        conversionRates: {},

        // Inventory State
        currentQuantity: 0,
        minimumQuantity: 0,
        parLevel: 0,
        maximumQuantity: 0,
        reorderPoint: 0,
        reorderQuantity: 0,

        // Cost Tracking
        currentUnitCost: 0,
        averageUnitCost: 0,
        lastPurchaseCost: 0,
        inventoryValue: 0,
        costHistory: [],
        vendorSpecificCosts: [],

        // Vendor / Purchasing
        preferredVendorId: '',
        vendorItemCodes: {},
        vendorPackaging: '',
        leadTimeDays: 0,
        lastOrderedDate: null,
        lastReceivedDate: null,

        // Recipe Relationships
        usedInRecipes: [],
        recipeUsageRate: 0,

        // Live Depletion
        theoreticalRemaining: 0,
        actualRemaining: 0,
        variance: 0,

        // Waste / Spoilage
        wasteTrackingEnabled: false,
        wasteCategory: '',
        averageWasteRate: 0,
        shelfLifeDays: 0,
        expirationDateTracking: false,

        // Counting System
        lastCountedAt: null,
        lastCountedBy: '',
        countFrequency: 'weekly',
        cycleCountGroup: '',
        countVarianceHistory: [],

        // Forecasting
        averageDailyUsage: 0,
        averageWeeklyUsage: 0,
        seasonalUsagePatterns: {},
        predictedRunoutDate: null,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await setDoc(ingRef, ingData);
      const created = { id: ingRef.id, ...ingData };
      setAllIngredients(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      set('ingredients', [...(item.ingredients || []), {
        ingredientId: ingRef.id, name: ingData.name, quantity: qty, unit: ingData.recipeUnit,
      }]);
      resetIngPicker();
    } catch {
      setIngError('Failed to create ingredient. Try again.');
    } finally {
      setIngCreating(false);
    }
  };

  const removeIngredient = (ingId) => {
    set('ingredients', (item.ingredients || []).filter(i => i.ingredientId !== ingId));
  };

  const set = (field, value) => setItem(i => ({ ...i, [field]: value }));

  const addSelector = () => {
    if (!selectorDraft.name || selectorDraft.price === '') return setError('Selector needs a name and price.');
    setOptionDraft(o => ({ ...o, selectors: [...o.selectors, { name: selectorDraft.name.trim(), price: selectorDraft.price }] }));
    setSelectorDraft({ name: '', price: '' });
    setError('');
  };

  const removeSelector = (idx) => {
    setOptionDraft(o => ({ ...o, selectors: o.selectors.filter((_, i) => i !== idx) }));
  };

  const saveOption = () => {
    if (!optionDraft.optionName.trim()) return setError('Option name is required.');
    setItem(i => ({ ...i, options: [...(i.options || []), { ...optionDraft, optionName: optionDraft.optionName.trim() }] }));
    setOptionDraft({ ...EMPTY_OPTION });
    setSelectorDraft({ name: '', price: '' });
    setShowOptionForm(false);
    setError('');
  };

  const removeOption = (idx) => {
    setItem(i => ({ ...i, options: i.options.filter((_, j) => j !== idx) }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    const ok = await onSave(item, originalId);
    if (!ok) setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90dvh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <h3 className="text-base font-semibold text-neutral-800">{isNew ? 'Add Item' : `Edit — ${initialItem.name}`}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-lg leading-none">✕</button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">

          {/* Basic fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Field label="Item Name *">
                <input type="text" value={item.name} onChange={e => set('name', e.target.value)}
                  placeholder="e.g. Pad Thai" className={inputCls} autoFocus={isNew} />
              </Field>
            </div>

            <Field label="Menu *">
              <select value={item.menu} onChange={e => set('menu', e.target.value)} className={inputCls}>
                <option value="">— select menu —</option>
                {menus.map(m => <option key={m.id} value={m.menuName}>{m.menuName}</option>)}
              </select>
            </Field>

            <Field label="Price ($) *">
              <input type="number" value={item.price} onChange={e => set('price', e.target.value)}
                placeholder="0.00" min="0" step="0.01" className={inputCls} />
            </Field>

            <div className="col-span-2">
              <Field label={`Category / Type${isNewType ? ' — new' : ''}`}>
                <div className="flex gap-2">
                  {!isNewType ? (
                    <select value={item.type} onChange={e => set('type', e.target.value)} className={inputCls}>
                      <option value="">— none —</option>
                      {existingTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  ) : (
                    <input type="text" value={item.type} onChange={e => set('type', e.target.value)}
                      placeholder="e.g. Appetizer, Entree, Dessert" className={inputCls} autoFocus />
                  )}
                  <button type="button" onClick={() => { setIsNewType(v => !v); set('type', ''); }}
                    className="shrink-0 px-3 py-2 text-sm border border-neutral-300 rounded-lg text-neutral-600 hover:border-neutral-500 transition-colors">
                    {isNewType ? 'Pick existing' : 'New type'}
                  </button>
                </div>
              </Field>
            </div>

            <div className="col-span-2">
              <Field label="Description">
                <input type="text" value={item.description} onChange={e => set('description', e.target.value)}
                  placeholder="Brief description shown on POS and customer-facing menus" className={inputCls} />
              </Field>
            </div>

            <div className="col-span-2">
              <ImageUploadField
                label="Item Image (optional)"
                value={item.imageUrl || ''}
                onChange={url => set('imageUrl', url)}
                restaurantId={restaurantId}
              />
            </div>
          </div>

          {/* Boolean toggles */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 pt-1">
            <Toggle value={item.availability !== false} onChange={v => set('availability', v)} label="Available" />
            <Toggle value={!!item.outOfStock} onChange={v => set('outOfStock', v)} label="Out of Stock (86'd)" />
            <Toggle value={!!item.showPicture} onChange={v => set('showPicture', v)} label="Show Picture on Web Menu" />
            <Toggle value={!!item.isDrinkItem} onChange={v => set('isDrinkItem', v)} label="Drink Item" />
            <Toggle value={!!item.isBarItem} onChange={v => set('isBarItem', v)} label="Bar Item" />
            <Toggle value={!!item.isDessertItem} onChange={v => set('isDessertItem', v)} label="Dessert Item" />
            <Toggle value={!!item.isAlcohol} onChange={v => set('isAlcohol', v)} label="Alcohol (21+)" />
            <Toggle value={!!item.isFeatured} onChange={v => set('isFeatured', v)} label="Featured (GBP / SEO)" />
          </div>

          {/* Course picker — only shown when courses are enabled for this restaurant */}
          {coursesEnabled && restaurantCourses.length > 0 && (
            <div className="border-t border-neutral-100 pt-4">
              <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-2">Default Course</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => set('defaultCourse', '')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    !item.defaultCourse
                      ? 'bg-neutral-900 text-white border-neutral-900'
                      : 'bg-white text-neutral-600 border-neutral-300 hover:border-neutral-500'
                  }`}
                >
                  None
                </button>
                {restaurantCourses.map(course => {
                  const isSelected = item.defaultCourse === course.id;
                  const textColor = isSelected ? (getContrastTextColor(course.color || '#5f799a')) : '#374151';
                  return (
                    <button
                      key={course.id}
                      type="button"
                      onClick={() => set('defaultCourse', course.id)}
                      style={isSelected ? { backgroundColor: course.color || '#5f799a', borderColor: course.color || '#5f799a', color: textColor } : {}}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        isSelected
                          ? ''
                          : 'bg-white border-neutral-300 text-neutral-700 hover:border-neutral-500'
                      }`}
                    >
                      {course.label}{course.autoFire ? ' ⚡' : ''}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-neutral-400 mt-1.5">Sets the default course when this item is added to an order. Staff can change it per-order.</p>
            </div>
          )}

          {/* Options list */}
          <div className="border-t border-neutral-100 pt-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">
                Modifier Options {item.options?.length > 0 ? `(${item.options.length})` : ''}
              </p>
              {!showOptionForm && (
                <button type="button" onClick={() => setShowOptionForm(true)}
                  className="text-sm px-3 py-1.5 border border-neutral-300 rounded-lg text-neutral-600 hover:border-neutral-500 transition-colors">
                  + Add Option
                </button>
              )}
            </div>

            {item.options?.map((opt, idx) => (
              <div key={idx} className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2.5 flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-neutral-800">{opt.optionName}</p>
                  <p className="text-sm text-neutral-400 mt-0.5">
                    {opt.required ? 'Required' : 'Optional'} · {opt.multiSelect ? 'Multi-select' : 'Single-select'}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {opt.selectors.map((sel, si) => (
                      <span key={si} className="text-sm bg-white border border-neutral-200 rounded px-1.5 py-0.5">
                        {sel.name}{parseFloat(sel.price) > 0 ? ` +$${parseFloat(sel.price).toFixed(2)}` : ''}
                      </span>
                    ))}
                  </div>
                </div>
                <button onClick={() => removeOption(idx)}
                  className="text-sm text-neutral-300 hover:text-red-500 transition-colors leading-none mt-0.5">✕</button>
              </div>
            ))}

            {/* Inline option builder */}
            {showOptionForm && (
              <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-neutral-700">New Option</p>

                <Field label="Option Name *">
                  <input type="text" value={optionDraft.optionName}
                    onChange={e => setOptionDraft(o => ({ ...o, optionName: e.target.value }))}
                    placeholder="e.g. Spice Level, Protein, Add-ons" className={inputCls} autoFocus />
                </Field>

                <div className="flex gap-6">
                  <Toggle value={optionDraft.required}
                    onChange={v => setOptionDraft(o => ({ ...o, required: v }))} label="Required Selection" />
                  <Toggle value={optionDraft.multiSelect}
                    onChange={v => setOptionDraft(o => ({ ...o, multiSelect: v }))} label="Multi-select" />
                </div>

                {/* Selectors */}
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-neutral-500">Selectors (choices)</p>
                  {optionDraft.selectors.map((sel, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-neutral-700 bg-white border border-neutral-200 rounded-lg px-3 py-1.5">
                      <span className="flex-1">{sel.name}</span>
                      <span className="text-neutral-400">{parseFloat(sel.price) > 0 ? `+$${parseFloat(sel.price).toFixed(2)}` : 'No charge'}</span>
                      <button onClick={() => removeSelector(i)} className="text-neutral-300 hover:text-red-400 transition-colors">✕</button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input type="text" value={selectorDraft.name}
                      onChange={e => setSelectorDraft(s => ({ ...s, name: e.target.value }))}
                      placeholder="Choice name" className={`${inputCls} flex-1`} />
                    <input type="number" value={selectorDraft.price}
                      onChange={e => setSelectorDraft(s => ({ ...s, price: e.target.value }))}
                      placeholder="Extra $" min="0" step="0.01" className={`${inputCls} w-24`} />
                    <button onClick={addSelector}
                      className="px-3 py-2 text-sm font-semibold bg-neutral-200 hover:bg-neutral-300 rounded-lg transition-colors whitespace-nowrap">
                      + Add
                    </button>
                  </div>
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <div className="flex gap-2">
                  <button onClick={saveOption}
                    className="px-4 py-1.5 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 transition-colors">
                    Save Option
                  </button>
                  <button onClick={() => { setShowOptionForm(false); setOptionDraft({ ...EMPTY_OPTION }); setError(''); }}
                    className="px-4 py-1.5 text-sm text-neutral-500 hover:text-neutral-700">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Ingredients section */}
          <div className="border-t border-neutral-100 pt-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">
                Ingredients{item.ingredients?.length > 0 ? ` (${item.ingredients.length})` : ''}
              </p>
              {!ingPickerOpen && (
                <button type="button" onClick={() => setIngPickerOpen(true)}
                  className="text-sm px-3 py-1.5 border border-neutral-300 rounded-lg text-neutral-600 hover:border-neutral-500 transition-colors">
                  + Link Ingredient
                </button>
              )}
            </div>

            {/* Linked ingredients */}
            {item.ingredients?.map(ing => (
              <div key={ing.ingredientId} className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-neutral-800">{ing.name}</span>
                  <span className="text-sm text-neutral-400 ml-2">{ing.quantity} {ing.unit}</span>
                </div>
                <button type="button" onClick={() => removeIngredient(ing.ingredientId)}
                  className="text-neutral-300 hover:text-red-500 transition-colors leading-none">✕</button>
              </div>
            ))}

            {/* Inline picker */}
            {ingPickerOpen && (
              <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 space-y-3">
                {!isNewIng ? (
                  <>
                    <p className="text-sm font-semibold text-neutral-700">Link Ingredient</p>

                    <input
                      type="text"
                      value={ingSearch}
                      onChange={e => { setIngSearch(e.target.value); setPickedIng(null); setIngQty(''); }}
                      placeholder="Search ingredients…"
                      className={inputCls}
                      autoFocus
                    />

                    {filteredIngs.length > 0 && (
                      <div className="max-h-40 overflow-y-auto border border-neutral-200 rounded-lg bg-white divide-y divide-neutral-100">
                        {filteredIngs.map(ing => (
                          <button
                            key={ing.id}
                            type="button"
                            onClick={() => { setPickedIng(ing); setIngSearch(ing.name); setIngError(''); }}
                            className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${
                              pickedIng?.id === ing.id
                                ? 'bg-neutral-900 text-white'
                                : 'hover:bg-neutral-50 text-neutral-700'
                            }`}
                          >
                            <span>{ing.name}</span>
                            <span className={`text-xs ${pickedIng?.id === ing.id ? 'text-neutral-300' : 'text-neutral-400'}`}>{ing.recipeUnit || ing.baseUnit || ing.unit}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {allIngredients.length === 0 && (
                      <p className="text-sm text-neutral-400 text-center py-1">No ingredients yet — create one below.</p>
                    )}
                    {filteredIngs.length === 0 && ingSearch.trim() && (
                      <p className="text-sm text-neutral-400 text-center py-1">No matches for "{ingSearch}".</p>
                    )}

                    {pickedIng && (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={ingQty}
                          onChange={e => { setIngQty(e.target.value); setIngError(''); }}
                          placeholder={`Qty (${pickedIng.recipeUnit || pickedIng.baseUnit || pickedIng.unit})`}
                          min="0.001"
                          step="0.001"
                          className={`${inputCls} w-36`}
                          autoFocus
                        />
                        <span className="text-sm text-neutral-400">{pickedIng.recipeUnit || pickedIng.baseUnit || pickedIng.unit} per serving</span>
                      </div>
                    )}

                    <button type="button" onClick={() => { setIsNewIng(true); setIngError(''); }}
                      className="text-sm text-neutral-500 hover:text-neutral-800 transition-colors">
                      + Create new ingredient
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-neutral-700">New Ingredient</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Field label="Name *">
                          <input type="text" value={newIngDraft.name}
                            onChange={e => setNewIngDraft(d => ({ ...d, name: e.target.value }))}
                            placeholder="e.g. Chicken Breast" className={inputCls} autoFocus />
                        </Field>
                      </div>
                      <Field label="Unit *">
                        <select value={newIngDraft.unit}
                          onChange={e => setNewIngDraft(d => ({ ...d, unit: e.target.value }))}
                          className={inputCls}>
                          {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </Field>
                      <Field label="Category">
                        <select value={newIngDraft.category}
                          onChange={e => setNewIngDraft(d => ({ ...d, category: e.target.value }))}
                          className={inputCls}>
                          {INGREDIENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Field>
                      <div className="col-span-2">
                        <Field label={`Quantity per serving (${newIngDraft.unit}) *`}>
                          <input type="number" value={ingQty}
                            onChange={e => { setIngQty(e.target.value); setIngError(''); }}
                            placeholder="0.00" min="0.001" step="0.001" className={inputCls} />
                        </Field>
                      </div>
                    </div>
                  </>
                )}

                {ingError && <p className="text-sm text-red-600">{ingError}</p>}

                <div className="flex items-center gap-2 flex-wrap">
                  {!isNewIng ? (
                    <button type="button" onClick={linkIngredient}
                      disabled={!pickedIng || !ingQty}
                      className="px-4 py-1.5 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-40 transition-colors">
                      Add
                    </button>
                  ) : (
                    <button type="button" onClick={createAndLinkIngredient}
                      disabled={ingCreating}
                      className="px-4 py-1.5 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-40 transition-colors">
                      {ingCreating ? 'Creating…' : 'Create & Add'}
                    </button>
                  )}
                  <button type="button" onClick={resetIngPicker}
                    className="px-4 py-1.5 text-sm text-neutral-500 hover:text-neutral-700">
                    Cancel
                  </button>
                  {isNewIng && (
                    <button type="button" onClick={() => { setIsNewIng(false); setIngError(''); }}
                      className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors">
                      ← Back to search
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {(parentError || error) && !showOptionForm && (
            <p className="text-sm text-red-600">{parentError || error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-100 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-neutral-500 hover:text-neutral-700">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors">
            {saving ? 'Saving…' : isNew ? 'Add Item' : 'Update Item'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MenusView;
